import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FaGripVertical } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import { userCanAdmin_Invoices } from '../../features/dash/views/finance/Invoices/InvoicesView';
import {
  useDeleteInvoiceItemMutation,
  useMoveInvoiceItemsMutation
} from '../../services/endpoints/finance/invoiceItem';
import { getCurrentUser } from '../../services/helper';
import { getErrorMessage } from '../../utils/utils';
import ConfirmationModal from '../modals/ConfirmationModal';
import CurrencyCell from './cellComponents/CurrencyCell';
import { LinkCell } from './cellComponents/LinkCell';
import './InvoiceItems.css';

// Define Invoice Item type
interface InvoiceItem {
  id: number;
  description: string;
  enrollment_id: number;
  unit_price: number;
  quantity: number;
}

const DeleteInvoiceItemButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteInvoiceItem, { isLoading }] = useDeleteInvoiceItemMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteInvoiceItem(id).unwrap();
      addToast('Invoice item deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Invoice Item"
        message="Are you sure you want to delete this invoice item?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

// Drag and Drop Item Type
const DRAGGABLE_ITEM_TYPE = 'row';

// Draggable Row Component
const DraggableRow = ({ item, index, moveRow, isPaidInvoice }: any) => {
  const [{ isDragging }, drag] = useDrag({
    type: DRAGGABLE_ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const [, drop] = useDrop({
    accept: DRAGGABLE_ITEM_TYPE,
    hover: (draggedItem: any) => {
      if (draggedItem.index !== index) {
        moveRow(draggedItem.index, index);
        draggedItem.index = index;
      }
    }
  });

  return (
    <tr
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move'
      }}
    >
      <td>
        <FaGripVertical style={{ cursor: 'grab' }} />
      </td>
      <td>{item.id}</td>
      <td>
        {item.description?.split(/\r\n|\n|\r/).map((line: any, index: any) => (
          <div key={index}>{line}</div>
        ))}
      </td>
      <td>
        <LinkCell
          content={item.enrollment_id ? `${item.enrollment_id}` : 'N/A'}
          url={item.enrollment_id ? `/admin/enrollments/${item.enrollment_id}` : ''}
          underline
        />
      </td>
      <td>
        <CurrencyCell value={item.unit_price} />
      </td>
      <td>{item.quantity}</td>
      <td>
        <CurrencyCell value={item.unit_price * (item.quantity ?? 0)} />
      </td>
      <td>
        <Stack
          direction="horizontal"
          gap={2}
        >
          <LinkCell
            content="View"
            url={`/admin/finance/invoice-items/${item.id}`}
            underline
          />
          {userCanAdmin_Invoices(getCurrentUser()) && !isPaidInvoice && (
            <>
              <LinkCell
                content="Edit"
                url={`/admin/finance/invoice-items/${item.id}/edit`}
                underline
              />
              <DeleteInvoiceItemButton id={item.id} />
            </>
          )}
        </Stack>
      </td>
    </tr>
  );
};

interface InvoiceItemsProps {
  data: any;
  isPaidInvoice: boolean;
}

// Table used for expanded rows information
export const InvoiceItems: React.FC<InvoiceItemsProps> = ({ data, isPaidInvoice }) => {
  const [tableData, setTableData] = useState(data);
  useEffect(() => {
    setTableData(data);
  }, [data]);

  const [moveInvoice] = useMoveInvoiceItemsMutation();

  const debouncedMoveRecipe = useCallback(
    debounce((updatedRows) => {
      moveInvoice({
        data: updatedRows.map((recipe: any, index: number) => ({
          id: recipe.id,
          position: index + 1
        }))
      });
    }, 500),
    [moveInvoice]
  );

  const moveRow = useCallback(
      (fromIndex: number, toIndex: number) => {
        const updatedData = [...tableData];
        const [movedRow] = updatedData.splice(fromIndex, 1);
        updatedData.splice(toIndex, 0, movedRow);
        setTableData(updatedData);
        if (fromIndex !== toIndex) {
          debouncedMoveRecipe(updatedData);
        }
      },
      [tableData]
  );

  const subtotal = tableData.reduce(
      (sum: number, item: InvoiceItem) =>
          sum + (item.unit_price ?? 0) * (item.quantity ?? 0),
      0
  );


  return (
    <DndProvider backend={HTML5Backend}>
      <div className="table-responsive">
        <table className="sfc-table simple-table table">
          <thead className="table-light">
            <tr>
              <th></th>
              <th>ID</th>
              <th>Description</th>
              <th>Enrollment</th>
              <th>Unit Price</th>
              <th>Quantity</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
          {tableData.map((item: InvoiceItem, idx: any) => (
              <DraggableRow
                  key={item.id}
                  item={item}
                  index={idx}
                  moveRow={moveRow}
                  isPaidInvoice={isPaidInvoice}
              />
          ))}
          </tbody>
          {tableData.length > 0 && (
              <tfoot>
              <tr>
                <td colSpan={6} className="text-end fw-bold">
                  Subtotal
                </td>
                <td className="fw-bold">
                  <CurrencyCell value={subtotal} />
                </td>
                <td></td>
              </tr>
              </tfoot>
          )}
        </table>
      </div>
    </DndProvider>
  );
};
